import { ContentGenerationWatcher } from 'components/GenerationStatus/ContentGenerationWatcher'
import { useCarouselDots } from 'hooks/useCarouselDots'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { Close, Loader1 } from 'blaze-ui/icons'
import { useElapsedTime } from 'components/GenerationStatus/GenerationStatusNotification'
import Styles from './MobileGenerationAndQuestionnaire.module.scss'
import { pluralize } from 'utils'
import { Text } from 'blaze-ui/Text/Text'
import { CalendarEventCard } from 'screens/Calendar/WeekViewV2/CalendarEventCard/CalendarEventCard'
import { CarouselDots } from 'components/Previews/InstagramPreview/CarouselDots'
import { Button } from 'blaze-ui/Button/Button'
import { useEvents } from 'hooks/calendar/useEvents'
import { Check02 } from 'blaze-ui/icons/16'
import { useEffect, useRef, useState } from 'react'
import { Heading } from 'blaze-ui/Heading/Heading'
import { UserClient } from 'api/clients/UserClient'
import { useAuth } from 'hooks/UseAuth'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { QUESTIONNAIRE_SNAP_POINT, QuestionnaireDrawer } from './QuestionnaireDrawer'
import cx from 'classnames'

export const MobileGenerationAndQuestionnaire = observer(() => {
  const [isQuestionnaireCompleted, setIsQuestionnaireCompleted] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [showEmailSentScreen, setShowEmailSentScreen] = useState(false)
  const { contentGeneration: store, drawersStore } = useRootStore()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const auth = useAuth()
  const completedRef = useRef(false)
  const generatedRef = useRef(false)
  const postsContainerRef = useRef<HTMLDivElement>(null)

  const elapsed = useElapsedTime(store.startedAt)
  const displayTotal = store.hasScheduleData ? store.totalCount : store.numberOfNewPosts
  const finishedGenerating = generatedRef.current

  const { allEvents: posts } = useEvents({
    currentMonth: { year: 2026, month: 3 },
  })

  const sendEmail = () => {
    // eslint-disable-next-line no-restricted-properties
    UserClient.sendMobileAutopilotDesktopLinkEmail(window.location.href)
  }

  const handleEmail = () => {
    drawersStore.closeDrawer()

    if (!emailSent) {
      sendEmail()
    }
    setTimeout(() => {
      setEmailSent(true)
      setShowEmailSentScreen(true)
    }, 350)
  }

  useEffect(() => {
    if (
      (store.isGenerationDone || (displayTotal === store.completedCount && store.completedCount > 0)) &&
      !generatedRef.current
    ) {
      generatedRef.current = true
    }
  }, [store.isGenerationDone, store.totalCount, store.numberOfNewPosts, store.completedCount, displayTotal])

  useEffect(() => {
    if (completedRef.current) return

    completedRef.current = true
    WorkspaceClient.completeVirtualMarketerOnboarding(currentWorkspaceId)
    drawersStore.openDrawer(QuestionnaireDrawer, {
      onComplete: () => {
        setIsQuestionnaireCompleted(true)
      },
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const postsSortedByHeight = posts.map((post) => ({ ...post })).sort((a, b) => (b.height ?? 0) - (a.height ?? 0))
  const aspectRatio =
    postsSortedByHeight[0]?.width && postsSortedByHeight[0]?.height
      ? postsSortedByHeight[0]?.width / postsSortedByHeight[0]?.height
      : 0
  const gapBetweenCards = 20
  const dotsHeight = 36
  const postsContainerHeight = postsContainerRef.current?.offsetHeight ?? 0
  const maxCardWidth = window.innerWidth * 0.7
  const aspectRatioCardWidth = aspectRatio ? aspectRatio * (postsContainerHeight - dotsHeight) : 255
  const cardWidth = Math.min(maxCardWidth, aspectRatioCardWidth)

  const {
    containerRef: postsCarouselRef,
    activeIndex: activeDotIndex,
    scrollToIndex: handleDotClick,
  } = useCarouselDots({
    itemCount: posts.length,
    itemWidth: cardWidth + gapBetweenCards,
  })

  return (
    <div className={Styles.rootContainer}>
      <div className={cx(Styles.root, { [Styles.questionnaireCompleted]: isQuestionnaireCompleted })}>
        {showEmailSentScreen && (
          <div className={Styles.fixedRoot}>
            <div className={Styles.closeButton}>
              <IconButton
                variant="tertiary"
                onPress={() => {
                  setShowEmailSentScreen(false)
                  drawersStore.openDrawer(QuestionnaireDrawer, {
                    initialSnapPoint: QUESTIONNAIRE_SNAP_POINT,
                    onComplete: () => {
                      setIsQuestionnaireCompleted(true)
                    },
                  })
                }}
                icon={Close}
                size="lg"
                color="var(--light-90)"
              />
            </div>
            <div className={Styles.fixedRootContent}>
              <div className={Styles.checkmarkBig}>
                <Check02 size={13} color="var(--light-100)" />
              </div>
              <div className={Styles.textContent}>
                <Heading level={2} color="var(--light-100)">
                  Check your inbox
                </Heading>
                <Text color="var(--light-100)">
                  Blaze works best on desktop. Open this email on your computer to review, edit, and post your content.
                </Text>
              </div>
            </div>
            <div className={Styles.bottomEmailSentText}>
              <Text color="var(--light-60)">We sent a link to {auth?.email}</Text>
            </div>
          </div>
        )}
        <div className={Styles.header}>
          <div className={Styles.leftSide}>
            {finishedGenerating ? (
              <div className={Styles.checkmark}>
                <Check02 size={9} color="var(--light-100)" />
              </div>
            ) : (
              <div className={Styles.spinnerWrap}>
                <Loader1 size={24} color="var(--light-60)" />
              </div>
            )}
            <div className={Styles.textContainer}>
              <Text variant="secondary" color="var(--light-100)">
                {finishedGenerating ? 'All finished up!' : 'Generating your content'}
              </Text>
              <Text variant="secondary" color="var(--light-60)">
                {finishedGenerating
                  ? 'Review your new content'
                  : `Generated ${store.completedCount}/${displayTotal} ${pluralize('post', displayTotal)}`}
              </Text>
            </div>
          </div>
          {!finishedGenerating && (
            <div className={Styles.rightSide}>
              <Text variant="secondary" color="var(--light-100)">
                {elapsed}
              </Text>
            </div>
          )}
        </div>
        <div className={Styles.postsContainer}>
          <div ref={postsContainerRef} className={Styles.postsCarouselWrapper}>
            <div ref={postsCarouselRef} className={Styles.postsCarousel}>
              {postsSortedByHeight.map((post, index) => (
                <div key={post.id} data-carousel-post data-carousel-index={index}>
                  <CalendarEventCard
                    event={post}
                    cardWidth={cardWidth}
                    loadingColor="var(--light-100)"
                    showStatus={false}
                    showHoverOverlay={false}
                    cardBorder="none"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className={Styles.carouselDots}>
            <CarouselDots
              count={posts.length}
              activeIndex={activeDotIndex}
              setActiveIndex={handleDotClick}
              activeDotColor="var(--light-100)"
              inactiveDotColor="var(--light-20)"
            />
          </div>
        </div>
        <div className={Styles.emailMeButtonContainer}>
          <Button size="lg" fullWidth onPress={handleEmail}>
            {emailSent ? 'Email sent' : 'Email me a link to edit and post'}
          </Button>
          {emailSent && (
            <Text color="var(--light-60)">
              Didn't get the email?{' '}
              <button type="button" onClick={sendEmail} className={Styles.tryAgainButton}>
                Try again
              </button>
            </Text>
          )}
        </div>
        <ContentGenerationWatcher />
      </div>
    </div>
  )
})
